import {
    Box,
    Button,
    Chip,
    Typography,
    useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { resolveCourseImage } from "../helper/resolveMediaUrl";

function getBannerTitle(banner, isAr) {
    if (isAr) {
        return banner.name_ar || banner.related_data?.name_ar || banner.name_en || banner.related_data?.name_en || "";
    }
    return banner.name_en || banner.related_data?.name_en || banner.name_ar || banner.related_data?.name_ar || "";
}

function getBannerDescription(banner, isAr) {
    const direct = isAr ? banner.description_ar : banner.description_en;
    if (direct) return direct;
    return isAr ? banner.description_en : banner.description_ar;
}

export default function BannerCarousel({ banners = [], isAr = true, onBannerClick }) {
    const theme = useTheme();
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [imageFailed, setImageFailed] = useState({});
    const count = banners.length;

    const go = useCallback((step) => {
        if (count <= 1) return;
        setIndex((current) => (current + step + count) % count);
    }, [count]);

    useEffect(() => {
        setIndex(0);
    }, [count]);

    useEffect(() => {
        if (count <= 1 || paused) return undefined;
        const timer = setInterval(() => go(1), 3000);
        return () => clearInterval(timer);
    }, [count, paused, go, index]);

    const slides = useMemo(() => banners.map((banner) => ({
        banner,
        title: getBannerTitle(banner, isAr),
        description: getBannerDescription(banner, isAr),
        image: resolveCourseImage(banner),
        typeLabel: banner.category === "path"
            ? (isAr ? "مسار تعليمي" : "Learning path")
            : (isAr ? "دورة تدريبية" : "Course"),
    })), [banners, isAr]);

    if (!count) return null;

    return (
        <Box
            sx={{ mb: 3 }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            dir={isAr ? "rtl" : "ltr"}
        >
            <Box
                sx={{
                    position: "relative",
                    borderRadius: 4,
                    overflow: "hidden",
                    minHeight: { xs: 280, sm: 320, md: 360 },
                    boxShadow: "0 24px 60px rgba(13, 53, 140, 0.22)",
                    bgcolor: "#0D358C",
                }}
            >
                {slides.map((slide, slideIndex) => {
                    const isActive = slideIndex === index;
                    const slideShowImage = slide.image && !imageFailed[slide.banner.id];
                    return (
                        <Box
                            key={slide.banner.id}
                            onClick={() => onBannerClick?.(slide.banner)}
                            sx={{
                                position: isActive ? "relative" : "absolute",
                                inset: 0,
                                opacity: isActive ? 1 : 0,
                                pointerEvents: isActive ? "auto" : "none",
                                transition: "opacity 0.65s ease, transform 0.65s ease",
                                transform: isActive ? "scale(1)" : "scale(1.03)",
                                cursor: onBannerClick ? "pointer" : "default",
                                minHeight: { xs: 280, sm: 320, md: 360 },
                            }}
                        >
                            {slideShowImage ? (
                                <Box
                                    component="img"
                                    src={slide.image}
                                    alt={slide.title}
                                    onError={() => setImageFailed((prev) => ({ ...prev, [slide.banner.id]: true }))}
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        background: "linear-gradient(135deg, #0D358C 0%, #2563eb 45%, #457b9d 100%)",
                                    }}
                                />
                            )}

                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    background: isAr
                                        ? "linear-gradient(105deg, rgba(4,14,38,0.88) 0%, rgba(13,53,140,0.72) 42%, rgba(13,53,140,0.25) 100%)"
                                        : "linear-gradient(255deg, rgba(4,14,38,0.88) 0%, rgba(13,53,140,0.72) 42%, rgba(13,53,140,0.25) 100%)",
                                }}
                            />

                            <Box
                                sx={{
                                    position: "relative",
                                    zIndex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    minHeight: { xs: 280, sm: 320, md: 360 },
                                    p: { xs: 3, sm: 4, md: 5 },
                                    maxWidth: { md: "68%" },
                                }}
                            >
                                <Chip
                                    icon={slide.banner.category === "path"
                                        ? <RouteOutlinedIcon sx={{ color: "#fff !important" }} />
                                        : <MenuBookOutlinedIcon sx={{ color: "#fff !important" }} />}
                                    label={slide.typeLabel}
                                    size="small"
                                    sx={{
                                        alignSelf: "flex-start",
                                        mb: 1.5,
                                        bgcolor: "rgba(255,255,255,0.14)",
                                        color: "#fff",
                                        fontWeight: 700,
                                        backdropFilter: "blur(8px)",
                                    }}
                                />

                                <Typography
                                    variant="h4"
                                    fontWeight={800}
                                    sx={{
                                        color: "#fff",
                                        lineHeight: 1.25,
                                        fontSize: { xs: "1.45rem", sm: "1.85rem", md: "2.15rem" },
                                        mb: 1.5,
                                    }}
                                >
                                    {slide.title}
                                </Typography>

                                {slide.description ? (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: "rgba(255,255,255,0.9)",
                                            lineHeight: 1.75,
                                            mb: 2.5,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            maxWidth: 560,
                                        }}
                                    >
                                        {slide.description}
                                    </Typography>
                                ) : null}

                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForwardIcon sx={{ transform: isAr ? "scaleX(-1)" : "none" }} />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onBannerClick?.(slide.banner);
                                    }}
                                    sx={{
                                        alignSelf: "flex-start",
                                        bgcolor: "#fff",
                                        color: "#0D358C",
                                        fontWeight: 700,
                                        px: 3,
                                        "&:hover": { bgcolor: "#f0f4ff" },
                                    }}
                                >
                                    {isAr ? "اكتشف الآن" : "Discover now"}
                                </Button>
                            </Box>
                        </Box>
                    );
                })}

                <Box
                    sx={{
                        position: "absolute",
                        top: 16,
                        insetInlineEnd: 16,
                        zIndex: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 999,
                        bgcolor: "rgba(255,255,255,0.14)",
                        backdropFilter: "blur(8px)",
                        color: "#fff",
                    }}
                >
                    <CampaignOutlinedIcon sx={{ fontSize: 18 }} />
                    <Typography variant="caption" fontWeight={700}>
                        {isAr ? "إعلانات المنصة" : "Platform ads"}
                    </Typography>
                </Box>
            </Box>

            {count > 1 ? (
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1.5 }}>
                    {slides.map((slide, dotIndex) => (
                        <Box
                            key={slide.banner.id}
                            component="button"
                            type="button"
                            aria-label={`${isAr ? "الإعلان" : "Ad"} ${dotIndex + 1}`}
                            onClick={() => setIndex(dotIndex)}
                            sx={{
                                width: dotIndex === index ? 28 : 10,
                                height: 10,
                                borderRadius: 999,
                                border: "none",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                bgcolor: dotIndex === index
                                    ? "#0D358C"
                                    : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.25)" : "#cbd5e1"),
                            }}
                        />
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}
